import React, { useCallback, useEffect, useRef, useState } from 'react';
import ImageGallery from '../HouseDetail/ImageGallery';
import OwnershipReviewedBadge from '../OwnershipReviewedBadge/OwnershipReviewedBadge';
import ShareListingButton from './ShareListingButton';
import ConditionReportButton, { SIGN_IN_NOTE, signInNote } from '../ConditionReport/ConditionReportButton';
import { PublicLink } from './PublicLayout';
import APIAgent from '../../agents/apiAgent';
import { fetchPublicListing } from '../../agents/publicAgent';
import { homeShareUrl, savePublicIntent } from '../../navigation/publicRoutes';
import { MLS_DATA_DISCLAIMER } from '../../utils/disclaimers';
import { isVacantLand, propertyTypeLabel } from '../../utils/propertyKind';
import { DEFAULT_PAGE_TITLE, setPublicPageMeta } from '../../utils/publicSite';
import styles from './PublicListingPage.module.css';

const formatPrice = (price) => (price ? `$${Math.round(price).toLocaleString('en-US')}` : 'Price not set');
const formatNumber = (value, digits = 0) => Number(value).toLocaleString('en-US', { maximumFractionDigits: digits });

/** The facts row, skipping anything the seller left blank. */
function listingFacts(listing) {
  const facts = [];
  if (!isVacantLand(listing)) {
    if (listing.bedrooms != null) facts.push({ label: listing.bedrooms === 1 ? 'bed' : 'beds', value: listing.bedrooms });
    if (listing.bathrooms != null) facts.push({ label: listing.bathrooms === 1 ? 'bath' : 'baths', value: formatNumber(listing.bathrooms, 2) });
    if (listing.square_feet) facts.push({ label: 'sq ft', value: formatNumber(listing.square_feet) });
  }
  if (listing.lot_size_acres) facts.push({ label: 'acre lot', value: formatNumber(listing.lot_size_acres, 2) });
  if (listing.year_built) facts.push({ label: 'built', value: listing.year_built });
  return facts;
}

const ACTION_COPY = {
  tour: {
    title: 'Sign in to request a tour',
    body: 'Tours and offers go to the owner directly, so they need a free cutthe6.com account. It takes about a minute.',
  },
  offer: {
    title: 'Sign in to make an offer',
    body: "You'll write, e-sign and track your offer in the app. Offers on cutthe6.com homes are free for buyers.",
  },
  report: {
    title: 'Sign in to view the condition report',
    body: SIGN_IN_NOTE,
  },
};

/**
 * Public, read-only page for one on-market listing (/homes/:id). Anyone can
 * view and share it; touring or making an offer asks the visitor to sign in
 * (or sign up) first, then App.js opens the listing in the app.
 *
 * @param {object} props
 * @param {number} props.homeId
 * @param {(path: string) => void} props.onNavigate
 * @param {(mode: 'login'|'register') => void} props.onRequireAccount
 */
export default function PublicListingPage({ homeId, onNavigate, onRequireAccount }) {
  const [state, setState] = useState({ status: 'loading', listing: null });
  const [prompt, setPrompt] = useState(null); // null | 'tour' | 'offer'
  const promptHeadingRef = useRef(null);

  const load = useCallback(async () => {
    setState({ status: 'loading', listing: null });
    try {
      const listing = await fetchPublicListing(homeId);
      setState({ status: 'ready', listing });
    } catch (err) {
      setState({ status: err?.status === 404 ? 'not-found' : 'error', listing: null });
    }
  }, [homeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const { listing } = state;
    if (state.status === 'ready' && listing) {
      const place = `${listing.address}, ${listing.city}, ${listing.state}`;
      setPublicPageMeta({
        title: `${listing.price ? `${formatPrice(listing.price)} · ` : ''}${place} | cutthe6.com`,
        description: `${listingFacts(listing).map((f) => `${f.value} ${f.label}`).join(' · ')}. ${listing.listing_status === 'pending' ? 'Under contract — for sale by owner' : 'For sale by owner'} on cutthe6.com.`,
        url: homeShareUrl(listing.id),
        image: listing.image_urls?.[0] ? APIAgent.getImageUrl(listing.image_urls[0]) : undefined,
      });
    } else if (state.status === 'not-found') {
      setPublicPageMeta({ title: `Home not available | ${DEFAULT_PAGE_TITLE}` });
    }
  }, [state]);

  useEffect(() => {
    if (prompt) promptHeadingRef.current?.focus();
  }, [prompt]);

  const continueWith = (mode) => {
    // The report opens from the listing in the app once they're signed in.
    const action = prompt && prompt !== 'report' ? prompt : 'view';
    savePublicIntent({ homeId, action });
    onRequireAccount(mode);
  };

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.inner} role="status" aria-live="polite">
          <div className={styles.skeletonGallery} />
          <div className={styles.skeletonLine} />
          <div className={`${styles.skeletonLine} ${styles.skeletonShort}`} />
          <span className={styles.srOnly}>Loading this home…</span>
        </div>
      </div>
    );
  }

  if (state.status === 'not-found' || state.status === 'error') {
    const missing = state.status === 'not-found';
    return (
      <div className={styles.page}>
        <div className={`${styles.inner} ${styles.empty}`} data-testid={missing ? 'public-listing-not-found' : 'public-listing-error'}>
          <h1 className={styles.emptyTitle}>
            {missing ? "This home isn't available" : "We couldn't load this home"}
          </h1>
          <p className={styles.emptyBody}>
            {missing
              ? 'It may have sold, or the owner may have taken it off the market.'
              : 'Check your connection and try again.'}
          </p>
          <div className={styles.emptyActions}>
            {!missing && (
              <button type="button" className={styles.primary} onClick={load}>Try again</button>
            )}
            <PublicLink to="/" onNavigate={onNavigate} className={missing ? styles.primary : styles.secondary}>
              Go to cutthe6.com
            </PublicLink>
          </div>
        </div>
      </div>
    );
  }

  const { listing } = state;
  const facts = listingFacts(listing);
  const pending = listing.listing_status === 'pending';
  const typeLabel = listing.property_type ? propertyTypeLabel(listing.property_type) : null;

  return (
    <div className={styles.page}>
      <article className={styles.inner} data-testid="public-listing">
        <div className={styles.topRow}>
          <span className={`${styles.status} ${pending ? styles.statusPending : ''}`}>
            {pending ? 'Under contract' : 'For sale by owner'}
          </span>
          <ShareListingButton houseId={listing.id} address={listing.address} label="Share" />
        </div>

        <div className={styles.gallery}>
          {listing.image_urls?.length ? (
            <ImageGallery images={listing.image_urls} />
          ) : (
            <div className={styles.noPhotos}>The owner hasn&apos;t added photos yet.</div>
          )}
        </div>

        <div className={styles.layout}>
          <header className={styles.header}>
            <p className={styles.price} data-testid="public-listing-price">{formatPrice(listing.price)}</p>
            <h1 className={styles.address}>
              {listing.address}
              <span className={styles.cityLine}>{listing.city}, {listing.state} {listing.zip_code}</span>
            </h1>
            {facts.length > 0 && (
              <ul className={styles.facts} aria-label="Key facts">
                {facts.map((fact) => (
                  <li key={fact.label} className={styles.fact}>
                    <strong>{fact.value}</strong> {fact.label}
                  </li>
                ))}
              </ul>
            )}
            <div className={styles.metaRow}>
              {typeLabel && <span className={styles.metaChip}>{typeLabel}</span>}
              {listing.listing_number && <span className={styles.metaChip}>Listing #{listing.listing_number}</span>}
              <OwnershipReviewedBadge
                reviewed={listing.ownership_documents_reviewed}
                reviewedMonth={listing.ownership_reviewed_month}
                size="sm"
              />
            </div>
          </header>

          <div className={styles.details}>
            {listing.description && (
              <section className={styles.section} aria-labelledby="about-heading">
                <h2 id="about-heading" className={styles.sectionTitle}>About this home</h2>
                <p className={styles.description}>{listing.description}</p>
              </section>
            )}

            <p className={styles.fineprint}>
              Listed by the owner on cutthe6.com. The price, facts, photos and description come from the seller
              and are not verified by cutthe6.com. {MLS_DATA_DISCLAIMER} Equal Housing Opportunity: sellers must
              follow federal and Wisconsin fair housing laws.
            </p>
          </div>

          <aside className={styles.aside} aria-labelledby="interest-heading">
            <div className={styles.card}>
              <h2 id="interest-heading" className={styles.cardTitle}>Interested in this home?</h2>
              {pending && (
                <p className={styles.cardNote}>
                  The owner has accepted an offer. You can still ask for a tour or send an offer, but the
                  owner can&apos;t accept it unless the current deal ends.
                </p>
              )}
              <div className={styles.cardActions}>
                <button
                  type="button"
                  className={styles.primary}
                  onClick={() => setPrompt('tour')}
                  aria-expanded={prompt === 'tour'}
                  aria-controls="public-listing-prompt"
                >
                  Request a tour
                </button>
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => setPrompt('offer')}
                  aria-expanded={prompt === 'offer'}
                  aria-controls="public-listing-prompt"
                >
                  Make an offer
                </button>
                {/* The seller's signed condition report, before an offer.
                    Reading it needs an account, so it asks to sign in. */}
                <ConditionReportButton
                  houseId={listing.id}
                  conditionReport={listing.condition_report}
                  signedIn={false}
                  onRequireSignIn={() => setPrompt('report')}
                  buttonClassName={styles.secondary}
                />
              </div>

              <div id="public-listing-prompt" className={styles.promptSlot}>
                {prompt && (
                  <div className={styles.prompt} data-testid="public-listing-signin-prompt">
                    <h3 ref={promptHeadingRef} tabIndex={-1} className={styles.promptTitle}>
                      {ACTION_COPY[prompt].title}
                    </h3>
                    <p className={styles.promptBody}>{prompt === 'report' ? signInNote(listing.condition_report) : ACTION_COPY[prompt].body}</p>
                    <div className={styles.promptActions}>
                      <button type="button" className={styles.primary} onClick={() => continueWith('register')}>
                        Create a free account
                      </button>
                      <button type="button" className={styles.secondary} onClick={() => continueWith('login')}>
                        Sign in
                      </button>
                    </div>
                    <button type="button" className={styles.dismiss} onClick={() => setPrompt(null)}>
                      Not now
                    </button>
                  </div>
                )}
              </div>
              <p className={styles.cardFoot}>
                You deal directly with the owner. cutthe6.com is software, not a broker.
              </p>
            </div>
          </aside>
        </div>
      </article>
    </div>
  );
}
